import {
  ActivityStatusOptions,
  PracticeType,
  PracticeTypesPerInstitution,
} from './calendarModels';
import {
  PraksiskalenderAktivitetDto,
  PraksisklasserPrEmneDto,
} from '../api/generated/studiested-generated-api';

const practiceTypeFilterOnSearch =
  (searchPhrase: string) => (pt: PracticeType) => {
    const phraseLowerCase = searchPhrase.toLocaleLowerCase();
    return [pt.emneCode, pt.emneName, pt.name].some(
      (it) => it.toLocaleLowerCase().indexOf(phraseLowerCase) >= 0,
    );
  };

const practicePeriodsByActivityStatusFilter =
  (activityStatusFilter: string) => (activity: PraksiskalenderAktivitetDto) => {
    switch (activityStatusFilter) {
      case ActivityStatusOptions.ACTIVITIES_NOT_SHARED:
        return !activity.delt;
      case ActivityStatusOptions.ACTIVITIES_MISSING_NEED:
        return !activity.delt && activity.behov < 1;
      case ActivityStatusOptions.ACTIVITIES_MISSING_CAPACITY:
        return activity.delt && activity.kapasitet < activity.behov;
      case ActivityStatusOptions.ACTIVITIES_FILLED_CAPACITY:
        return activity.delt && activity.kapasitet >= activity.behov;
    }
  };

export const practicePeriodFilter = (
  practicePeriodsPerInstitutions: {
    [klasseId: string]: PraksiskalenderAktivitetDto[];
  },
  activityStatusFilter: string,
): { [klasseId: string]: PraksiskalenderAktivitetDto[] } => {
  if (
    activityStatusFilter === ActivityStatusOptions.ALL_ACTIVITIES ||
    activityStatusFilter === ActivityStatusOptions.UNFILTERED
  ) {
    return practicePeriodsPerInstitutions;
  }

  return Object.entries(practicePeriodsPerInstitutions).reduce(
    (
      acc: { [key: string]: PraksiskalenderAktivitetDto[] },
      [klasseId, practicePeriods],
    ) => {
      const filtered = practicePeriods.filter(
        practicePeriodsByActivityStatusFilter(activityStatusFilter),
      );

      if (filtered.length > 0) {
        return {
          ...acc,
          [klasseId]: filtered,
        };
      }

      return acc;
    },
    {},
  );
};

export const practiceTypeFiltersCombined = (
  practicePeriodsFiltered: {
    [klasseId: string]: PraksiskalenderAktivitetDto[];
  },
  practiceTypesPerInstitutions: PracticeTypesPerInstitution[],
  searchPhrase: string,
  activityStatusFilter: string,
) => {
  const filterRowsWithoutActivities =
    activityStatusFilter !== ActivityStatusOptions.UNFILTERED;

  // klasseIder som hører sammen innenfor samme emne og klasse (i praksis: undenheter per klasse i et emne)
  const asscociatedKlasseIds = practiceTypesPerInstitutions.flatMap((it) =>
    it.practiceTypes.flatMap((practiceType) => {
      // Gets different group letters A, B, C, etc.
      const groups = [
        ...new Set(practiceType.klasser.map((klasse) => klasse.bokstav)),
      ];
      const initReturnType = groups.map(() => []);

      // Separates undenheter per class to also be distinguished by group letters
      // i.e. separates classes into what should be shown on one line in the calendar
      return practiceType.klasser.reduce((acc: string[][], klasse) => {
        const klasseIdx = groups.indexOf(klasse.bokstav);

        return acc.map((groupArray, index) => {
          if (index === klasseIdx) {
            return groupArray.concat(klasse.id);
          }

          return groupArray;
        });
      }, initReturnType);
    }),
  );

  const klasseIdsAsscociatedWithFilter = asscociatedKlasseIds
    .filter((klasseIds) =>
      klasseIds.some((klasseId) => klasseId in practicePeriodsFiltered),
    )
    .flatMap((ids) => ids);

  return practiceTypesPerInstitutions
    .map((it) => {
      let filteredPracticeTypes = it.practiceTypes.filter(
        practiceTypeFilterOnSearch(searchPhrase),
      );

      if (filterRowsWithoutActivities) {
        filteredPracticeTypes = filteredPracticeTypes
          .map((practiceType) => {
            return {
              ...practiceType,
              klasser: practiceType.klasser.filter((klasse) =>
                klasseIdsAsscociatedWithFilter.includes(klasse.id),
              ),
            };
          })
          .filter((it) => it.klasser.length > 0);
      }

      filteredPracticeTypes.sort((a, b) =>
        a.name === b.name
          ? a.emneCode < b.emneCode
            ? -1
            : 1
          : a.name < b.name
          ? -1
          : 1,
      );

      return {
        ...it,
        practiceTypes: filteredPracticeTypes,
      };
    })
    .filter((it) => it.practiceTypes.length > 0);
};

export const groupActivitiesByClass = (
  aktiviteter: Array<PraksiskalenderAktivitetDto>,
): {
  [klasseId: string]: PraksiskalenderAktivitetDto[];
} => {
  return aktiviteter.reduce(
    (
      acc: { [klasseId: string]: PraksiskalenderAktivitetDto[] },
      v: PraksiskalenderAktivitetDto,
    ) => {
      (acc[v.klasseId] = acc[v.klasseId] || []).push(v);
      return acc;
    },
    {},
  );
};

export const groupClassesByInstitution = (
  klasser: Array<PraksisklasserPrEmneDto>,
): PracticeTypesPerInstitution[] => {
  const classPrInstitution = klasser.reduce(
    (
      acc: { [stedId: string]: PraksisklasserPrEmneDto[] },
      v: PraksisklasserPrEmneDto,
    ) => {
      (acc[v.sted.id] = acc[v.sted.id] || []).push(v);
      return acc;
    },
    {},
  );
  return Object.values(classPrInstitution).map((items) => {
    return {
      institutionName: items[0].sted.navn[0],
      institutionCodesSlug: items[0].sted.id,
      institutionNames: items[0].sted.navn,
      institutionCodes: [items[0].sted.id],
      practiceTypes: items.map((it) => {
        return {
          institutionCodesSlug: it.sted.id,
          institutionNames: it.sted.navn,
          institutionCodes: [it.sted.id],
          emneCode: it.emne.kode,
          emneName: it.emne.navn,
          name: it.praksis.navn,
          code: it.praksis.kode,
          klasser: it.klasser,
        };
      }),
    };
  });
};
